import { useAppDispatch, useAppSelector } from 'hooks/redux';
// CORE
import { DateTime } from 'luxon';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useRef } from 'react';
import { useEffectOnce } from 'react-use';

import Close from '@mui/icons-material/Close';
// MUI
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// APP-COMPONENTS
import { aggErrors, aggErrorsSelectors } from 'app-components/flux/account-discovery';
import type { accountsTypes } from 'app-components/flux/accounts';
import {
  institutionLoginsActions,
  institutionLoginsSelectors,
  institutionLoginsUtils,
} from 'app-components/flux/institution-logins';
import type { AggStatus, InstitutionLogin } from 'app-components/flux/institution-logins/institutionLoginsTypes';
import { institutionsSelectors } from 'app-components/flux/institutions';
import type { FiIssue } from 'app-components/flux/institutions/institutionsTypes';
import { tracker } from 'app-components/utils/core';

// LOCAL
import A2AEntryButton from 'components/A2AEntryButton';
import showManageUpdatesDialog from 'components/Dialogs/ManageUpdatesDialog/dispatcher';

type AggAction = {
  action: string;
  statusCode: string;
  statusText: string;
  status: AggStatus;
  title: string;
  text: string;
  code: string;
  actionLabel: string;
};

type InstitutionLoginStatusBannerProps = {
  institutionLoginId: string;
  /** Used for mixpanel tracking */
  location: 'account settings' | 'account list' | 'investments';
  /** Should we show both the fi issue & fi error if they both exist. Will automatically show both if issue is of type INFO*/
  showBoth: boolean;
  /** Add styles to the root element */
  className?: string;
  /** Is the status banner dismissible */
  dismissible?: boolean;
  /** Can we show the A2A entry point */
  showA2AEntry?: boolean;
  /** Hide the A2A entry point when account is A2A verified */
  hideA2AOnVerified?: boolean;
  /** Only show the A2A entry point when account is A2A verified */
  onlyA2AOnVerified?: boolean;
  /** The account we enter for the A2A entery point */
  account?: accountsTypes.Account;
};

const FiIssueBody = ({ fiIssue }: { fiIssue: FiIssue }) => {
  return (
    <>
      <AlertTitle>{fiIssue.shortMessage}</AlertTitle>
      {fiIssue.longMessage}
    </>
  );
};

const FiErrorBody = ({ aggAction }: { aggAction: AggAction }) => {
  return (
    <>
      <AlertTitle>{aggAction.title}</AlertTitle>
      <Stack>
        {aggAction.text}
        {aggAction.statusCode || aggAction.code ? (
          <Typography variant='captionRegular' sx={{ fontFamily: 'monospace' }}>
            {`(Care code: ${aggAction.statusCode || aggAction.code})`}
          </Typography>
        ) : null}
      </Stack>
    </>
  );
};

const FiLoginActionButton = ({
  institutionLogin,
  fiIssue,
  aggAction,
  dismissible,
  location,
  trackAction,
}: {
  institutionLogin: InstitutionLogin;
  fiIssue?: FiIssue;
  aggAction: AggAction;
  dismissible?: boolean;
  location: string;
  trackAction: (action: 'close' | 'refresh' | 'shown') => void;
}) => {
  const dispatch = useAppDispatch();

  const aggLoading = useAppSelector((state) =>
    institutionLoginsSelectors.getIdsRefreshing(state)?.includes(institutionLogin.id),
  );

  const removeFiStatusUntilNextSession = () => {
    if (fiIssue) {
      trackAction('close');
      dispatch(institutionLoginsActions.addDismissedFiIssue({ id: institutionLogin.id, type: fiIssue.issueType }));
    }
  };

  const kickoffReconnect = () => {
    if (fiIssue) {
      trackAction('refresh');
    }
    aggErrors.kickoffAggAction(dispatch, aggAction, institutionLogin, location);
  };

  const showActionButton =
    !fiIssue || fiIssue.issueType === 'PARTIAL_OUTAGE' || fiIssue.issueType === 'SCHEDULED_OUTAGE';

  const onManageUpdatesClick = useCallback(
    () => showManageUpdatesDialog({ defaultValue: institutionLogin.id }),
    [institutionLogin.id],
  );

  const isRefreshFrequencySupported = useAppSelector((state) =>
    institutionLoginsSelectors.isRefreshFrequencySupported(state, institutionLogin?.id || ''),
  );

  return (
    <Stack direction='row' spacing={2} alignItems='center'>
      {isRefreshFrequencySupported && (
        <Button size='small' id='manage-institution-login-updates' variant='text' onClick={onManageUpdatesClick}>
          Manage updates
        </Button>
      )}

      {aggLoading && showActionButton ? (
        <CircularProgress size={32} sx={{ marginY: 4, alignSelf: 'center' }} />
      ) : (
        <Stack direction={'row'} gap={2}>
          {showActionButton ? (
            <Button
              size='small'
              id={'accounts-fix-account-button'}
              variant={!fiIssue ? 'contained' : 'text'}
              onClick={kickoffReconnect}
            >
              {!fiIssue ? aggAction.actionLabel : 'Try again anyway'}
            </Button>
          ) : null}

          {fiIssue && dismissible ? (
            <IconButton
              size='small'
              aria-label='dismiss'
              id='fi-status-dismiss'
              onClick={removeFiStatusUntilNextSession}
            >
              <Close />
            </IconButton>
          ) : null}
        </Stack>
      )}
    </Stack>
  );
};

/** This banner is used to provide status & errors for an institution login */
export const InstitutionLoginStatusBanner = ({
  institutionLoginId,
  location,
  dismissible,
  showBoth,
  showA2AEntry,
  hideA2AOnVerified,
  onlyA2AOnVerified,
  account,
}: InstitutionLoginStatusBannerProps) => {
  const shownTracked = useRef(false);

  const institutionLogin = useAppSelector((state) =>
    institutionLoginsSelectors.getInstitutionLoginForId(state, institutionLoginId),
  );

  const fiIssue = useAppSelector((state) =>
    institutionLoginsSelectors.getFiIssueForLogin(state, institutionLoginId, !dismissible),
  );

  const aggAction: AggAction | undefined = useAppSelector((state) =>
    aggErrorsSelectors.getAggregationAction(state, { institutionLogin }),
  );

  const institution = useAppSelector((state) =>
    institutionLogin ? institutionsSelectors.getInstitution(state, institutionLogin.id) : undefined,
  );

  const trackAction = useCallback(
    (action: 'close' | 'refresh' | 'shown') => {
      if (fiIssue) {
        tracker.track(tracker.events.fiStatusBanner, {
          type: fiIssue.issueType,
          source: fiIssue.issueSource,
          channel: fiIssue.channel,
          location,
          institution_id: institutionLogin?.institutionId,
          institution_name: institution?.name || 'Unknown',
          aggregator: institution?.aggregator || 'Unknown',
          action,
        });
      }
    },
    [fiIssue, institution?.aggregator, institution?.name, institutionLogin?.institutionId, location],
  );

  useEffect(() => {
    // only track shown the first time we see an fiIssue
    if (!shownTracked.current && fiIssue) {
      trackAction('shown');
      shownTracked.current = true;
    }
  }, [fiIssue, trackAction]);

  const isAggError = institutionLogin && aggAction && institutionLoginsUtils.isErrorType(aggAction?.status);
  const shouldShowBoth = isAggError && fiIssue && (showBoth || fiIssue.issueType === 'INFO');
  const showInfoOnly = !isAggError && institutionLogin && fiIssue && fiIssue.issueType === 'INFO';

  let ret: ReactNode;

  if (shouldShowBoth) {
    ret = (
      <Stack gap={2}>
        <Paper>
          <Alert
            severity='info'
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <FiLoginActionButton
                  institutionLogin={institutionLogin}
                  fiIssue={fiIssue}
                  aggAction={aggAction}
                  dismissible={dismissible}
                  location={location}
                  trackAction={trackAction}
                />
              </Box>
            }
          >
            <FiIssueBody fiIssue={fiIssue} />
          </Alert>
        </Paper>
        <Paper>
          <Alert
            severity='warning'
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <FiLoginActionButton
                  institutionLogin={institutionLogin}
                  aggAction={aggAction}
                  location={location}
                  trackAction={trackAction}
                />
              </Box>
            }
          >
            <FiErrorBody aggAction={aggAction} />
          </Alert>
        </Paper>
      </Stack>
    );
  } else if (isAggError) {
    ret = (
      <Paper>
        <Alert
          severity='warning'
          action={
            <FiLoginActionButton
              institutionLogin={institutionLogin}
              fiIssue={fiIssue}
              aggAction={aggAction}
              dismissible={fiIssue && dismissible}
              location={location}
              trackAction={trackAction}
            />
          }
          sx={{
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            alignItems: { xs: 'flex-start', md: 'center' },
          }}
        >
          {fiIssue ? <FiIssueBody fiIssue={fiIssue} /> : <FiErrorBody aggAction={aggAction} />}
        </Alert>
      </Paper>
    );
  } else if (showInfoOnly) {
    ret = (
      <Alert severity={fiIssue ? 'info' : 'warning'}>
        <FiIssueBody fiIssue={fiIssue} />
      </Alert>
    );
  } else if (showA2AEntry && account && institutionLogin) {
    ret = (
      <A2AEntryButton
        account={account}
        institutionLogin={institutionLogin}
        hideOnVerified={hideA2AOnVerified}
        onlyShowVerified={onlyA2AOnVerified}
      />
    );
  } else {
    ret = null;
  }

  return ret;
};

type InstitutionStatusBannerProps = {
  institutionId: string;
  className?: string;
};

/** This banner is used to provide status for an entire institution  */
export const InstitutionStatusBanner = ({ institutionId }: InstitutionStatusBannerProps) => {
  const institution = useAppSelector((state) => institutionsSelectors.getInstitution(state, institutionId));

  const fiIssue = institution?.fiIssue;

  useEffectOnce(() => {
    if (fiIssue) {
      tracker.track(tracker.events.fiStatusBanner, {
        type: fiIssue.issueType,
        source: fiIssue.issueSource,
        channel: fiIssue.channel,
        location: 'addFi authenticator',
        institution_id: institution.id,
        institution_name: institution.name || 'Unknown',
        aggregator: institution.aggregator || 'Unknown',
        action: 'shown',
      });
    }
  });

  return fiIssue ? (
    <Alert severity='info' sx={{ marginY: 1 }}>
      <FiIssueBody fiIssue={fiIssue} />
    </Alert>
  ) : null;
};

/** This banner is used for Apple Wallet Institution Logins */
export const AppleWalletErrorBanner = ({ institutionLoginId }: { institutionLoginId?: string }) => {
  const institutionLogin = useAppSelector((state) =>
    institutionLoginsSelectors.getInstitutionLoginForId(state, institutionLoginId || ''),
  );
  const aggAction: AggAction | undefined = useAppSelector((state) =>
    aggErrorsSelectors.getAggregationAction(state, { institutionLogin }),
  );
  const isAggError = institutionLogin && aggAction && institutionLoginsUtils.isErrorType(aggAction?.status);

  const lastRefreshSuccessfulAt = institutionLogin?.aggregators?.first()?.lastRefreshSuccessfulAt;
  const lastSyncAt = lastRefreshSuccessfulAt ? DateTime.fromISO(lastRefreshSuccessfulAt) : null;

  const showWarning = !isAggError && lastSyncAt ? lastSyncAt.diffNow('days').days < -1 : false;

  const lastSyncText = lastSyncAt
    ? `Last synced: ${lastSyncAt.toFormat("HH:mm:ss 'on' MMM dd yyyy")}. You can sync the account using your iPhone.`
    : '';

  if (!showWarning && !isAggError) {
    return null;
  }

  return (
    <Alert severity={showWarning ? 'info' : 'warning'}>
      <AlertTitle>{showWarning ? 'Re-sync with Apple Wallet' : 'Apple Wallet access needed'}</AlertTitle>
      {showWarning
        ? lastSyncText
        : 'There was an issue connecting to Apple Wallet. Re-connect the account using your iPhone.'}
    </Alert>
  );
};
